export const M4_UNIT_5 = {
  id: "m4-u5-action-state-verbs",
  title: "Unidad 5: Action vs State",
  topic: "Master the difference between action and state verbs! Learn which verbs can use continuous (-ING) and which cannot. Essential for natural English and avoiding common mistakes like 'I am knowing' or 'I am needing'!",
  iconType: "activity",
  grammarImage: "/grammar/grammar-m4-u5.jpg",
  grammarText: "Action verbs = acciones físicas/visibles (run, eat, work) → pueden usar continuous. State verbs = estados mentales/emociones/posesión (know, like, want, have) → NO usan continuous. 'I am knowing' ❌, 'I know' ✓",
  
  sets: [
    // SET 1: Action Verbs (Review - Básico)
    {
      id: "m4-u5-set1",
      title: "Set 1: Action Verbs You Can See",
      questions: [
        {
          type: "mc",
          q: "Right now: 'She ___ her lunch.' (eat)",
          options: ["is eating", "eats", "eat"],
          a: "is eating",
          tip: "Action verbs = acciones físicas que puedes VER. Eating, running, working = action = pueden usar -ING!"
        },
        {
          type: "input",
          q: "Complete: 'They ___ soccer in the park.' (play - now)",
          a: "are playing"
        },
        {
          type: "order",
          q: "Ordena: 'Él está corriendo en el gimnasio'",
          words: ["He's", "running", "at", "the", "gym."],
          a: "He's running at the gym."
        },
        {
          type: "mc",
          q: "'I ___ my homework right now.'",
          options: ["am doing", "do", "doing"],
          a: "am doing"
        },
        {
          type: "match",
          q: "Match action verb to continuous form:",
          pairs: [
            { left: "work", right: "working" },
            { left: "cook", right: "cooking" },
            { left: "study", right: "studying" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ a movie.' (watch - now)",
          a: "are watching"
        },
        {
          type: "mc",
          q: "'He ___ to the store.'",
          options: ["is walking", "walks", "walk"],
          a: "is walking",
          successtip: "✓ ACTION VERB! Physical movement! 🚶 Walk, run, drive = you can SEE it = can use -ING!"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos limpiando la casa'",
          words: ["We're", "cleaning", "the", "house."],
          a: "We're cleaning the house."
        },
        {
          type: "input",
          q: "Complete: 'She ___ an email.' (write - now)",
          a: "is writing"
        },
        {
          type: "mc",
          q: "All of these are ACTION verbs you can use with -ING:",
          options: [
            "eat, sleep, work, drive",
            "know, like, want, need",
            "be, have, seem, appear"
          ],
          a: "eat, sleep, work, drive"
        }
      ]
    },

    // SET 2: More Action Verbs (Básico)
    {
      id: "m4-u5-set2",
      title: "Set 2: Daily Actions",
      questions: [
        {
          type: "mc",
          q: "'I ___ my teeth right now.'",
          options: ["am brushing", "brush", "brushing"],
          a: "am brushing"
        },
        {
          type: "input",
          q: "Complete: 'He ___ the dishes.' (wash - now)",
          a: "is washing"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está preparando el desayuno'",
          words: ["She's", "making", "breakfast."],
          a: "She's making breakfast."
        },
        {
          type: "mc",
          q: "'They ___ their car.'",
          options: ["are fixing", "fix", "fixing"],
          a: "are fixing",
          successtip: "✓ FIX! Phrasal verb alert! 🔧 'Fix up' = repair completely. 'I'm fixing up my car' = renovating!"
        },
        {
          type: "match",
          q: "Match activity to continuous:",
          pairs: [
            { left: "read", right: "reading" },
            { left: "dance", right: "dancing" },
            { left: "talk", right: "talking" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ for the bus.' (wait - now)",
          a: "are waiting"
        },
        {
          type: "mc",
          q: "'She ___ her hair.'",
          options: ["is drying", "dries", "drying"],
          a: "is drying"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy cargando mi teléfono'",
          words: ["I'm", "charging", "my", "phone."],
          a: "I'm charging my phone."
        },
        {
          type: "input",
          q: "Complete: 'He ___ his jacket.' (put on - now)",
          a: "is putting on"
        },
        {
          type: "mc",
          q: "'I ___ a shower right now.'",
          options: ["am taking", "take", "taking"],
          a: "am taking"
        }
      ]
    },

    // SET 3: Introducing State Verbs (Básico)
    {
      id: "m4-u5-set3",
      title: "Set 3: Verbs You Cannot See",
      questions: [
        {
          type: "mc",
          q: "'I ___ you.' (like - general truth)",
          options: ["like", "am liking", "liking"],
          a: "like",
          tip: "'Like' = state verb (emotion). State verbs = NO continuous! 'I like' ✓, 'I am liking' ❌"
        },
        {
          type: "input",
          q: "Complete: 'She ___ English.' (know)",
          a: "knows"
        },
        {
          type: "order",
          q: "Ordena: 'Necesito ayuda'",
          words: ["I", "need", "help."],
          a: "I need help.",
          tip: "'Need' = state verb. 'I need' ✓, 'I am needing' ❌. Common error for Spanish speakers!"
        },
        {
          type: "mc",
          q: "ERROR: 'I am wanting a coffee' - Corrección:",
          options: ["I want a coffee", "I wanting a coffee", "I wants a coffee"],
          a: "I want a coffee",
          tip: "'Want' = state verb (desire). Spanish 'Estoy queriendo' ≠ English! Use simple: 'I want' ✓"
        },
        {
          type: "match",
          q: "Match state verb to meaning:",
          pairs: [
            { left: "like", right: "emotion (gustar)" },
            { left: "know", right: "mental state (saber)" },
            { left: "want", right: "desire (querer)" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'They ___ your name.' (remember)",
          a: "remember"
        },
        {
          type: "mc",
          q: "'She ___ the answer.'",
          options: ["knows", "is knowing", "knowing"],
          a: "knows",
          successtip: "✓ KNOW! Mental state! 🧠 Know, understand, believe, think (opinion) = state verbs = simple only!"
        },
        {
          type: "order",
          q: "Ordena: 'Entiendo el problema'",
          words: ["I", "understand", "the", "problem."],
          a: "I understand the problem."
        },
        {
          type: "input",
          q: "Complete: 'He ___ this music.' (hate)",
          a: "hates"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "I am needing help",
            "I am working now",
            "She is eating lunch"
          ],
          a: "I am needing help",
          tip: "'Need' = state! 'I need' ✓. 'Working' and 'eating' = actions = can use -ING ✓"
        }
      ]
    },

    // SET 4: Common State Verbs (Básico)
    {
      id: "m4-u5-set4",
      title: "Set 4: Emotions & Mental States",
      questions: [
        {
          type: "mc",
          q: "'I ___ this movie is boring.' (think - opinion)",
          options: ["think", "am thinking", "thinking"],
          a: "think",
          tip: "'Think' for OPINION = state verb. 'I think it's good' ✓. (Note: 'I'm thinking about' = different!)"
        },
        {
          type: "input",
          q: "Complete: 'She ___ a new car.' (want)",
          a: "wants"
        },
        {
          type: "order",
          q: "Ordena: 'Me gusta tu camisa'",
          words: ["I", "like", "your", "shirt."],
          a: "I like your shirt."
        },
        {
          type: "mc",
          q: "ERROR: 'I am knowing him' - Corrección:",
          options: ["I know him", "I knowing him", "I knows him"],
          a: "I know him",
          tip: "Spanish 'Lo estoy conociendo' confuses! For 'I know him' (conozco) = simple always!"
        },
        {
          type: "match",
          q: "Match verb type:",
          pairs: [
            { left: "love (emotion)", right: "State verb" },
            { left: "run (action)", right: "Action verb" },
            { left: "believe (mental)", right: "State verb" }
          ],
          a: "completed",
          successtip: "✓ VERB TYPES! Key pattern! 📚 Emotions/thoughts/senses = STATE. Physical actions = ACTION!"
        },
        {
          type: "input",
          q: "Complete: 'We ___ our mistakes.' (realize)",
          a: "realize"
        },
        {
          type: "mc",
          q: "'He ___ his job.' (prefer)",
          options: ["prefers", "is preferring", "prefer"],
          a: "prefers"
        },
        {
          type: "order",
          q: "Ordena: 'Ella no entiende inglés todavía'",
          words: ["She", "doesn't", "understand", "English", "yet."],
          a: "She doesn't understand English yet."
        },
        {
          type: "input",
          q: "Complete: 'They ___ in God.' (believe)",
          a: "believe"
        },
        {
          type: "mc",
          q: "Which sentence is CORRECT?",
          options: [
            "I love pizza",
            "I am loving pizza",
            "I am love pizza"
          ],
          a: "I love pizza",
          tip: "'Love' = emotion = state verb = simple form! Exception: 'I'm loving it!' (McDonald's) = slang/marketing!"
        }
      ]
    },

    // SET 5: Possession Verbs (Intermedio)
    {
      id: "m4-u5-set5",
      title: "Set 5: Have & Belong",
      questions: [
        {
          type: "mc",
          q: "'I ___ a car.' (possession)",
          options: ["have", "am having", "having"],
          a: "have",
          tip: "'Have' for POSSESSION = state verb. 'I have a car' ✓. BUT 'I'm having lunch' (action) = OK!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ two jobs.' (have - possession)",
          a: "has"
        },
        {
          type: "order",
          q: "Ordena: 'Este libro me pertenece'",
          words: ["This", "book", "belongs", "to", "me."],
          a: "This book belongs to me.",
          tip: "'Belong' = always state! 'It belongs' ✓, 'It is belonging' ❌. Posesión = estado."
        },
        {
          type: "mc",
          q: "ERROR: 'I am having a car' - Corrección:",
          options: ["I have a car", "I having a car", "I has a car"],
          a: "I have a car",
          successtip: "✓ HAVE CONFUSION! Tricky verb! 🚗 'I HAVE a car' (possession) = simple. 'I'm HAVING lunch' (action) = continuous! Same verb, different meanings!"
        },
        {
          type: "match",
          q: "Match 'have' usage to form:",
          pairs: [
            { left: "I have a house (possession)", right: "Simple" },
            { left: "I'm having dinner (action)", right: "Continuous" },
            { left: "She has a brother (possession)", right: "Simple" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'This apartment ___ to my landlord.' (belong)",
          a: "belongs"
        },
        {
          type: "mc",
          q: "'The car ___ him $500 a month.' (cost)",
          options: ["costs", "is costing", "costing"],
          a: "costs",
          tip: "'Cost' = state verb (precio/valor). 'It costs' ✓, 'It is costing' ❌. Fixed value = state."
        },
        {
          type: "order",
          q: "Ordena: 'Tengo un problema con mi visa'",
          words: ["I", "have", "a", "problem", "with", "my", "visa."],
          a: "I have a problem with my visa."
        },
        {
          type: "input",
          q: "Complete: 'She ___ three cats and a dog.' (have)",
          a: "has"
        },
        {
          type: "mc",
          q: "Action or State? 'I'm having a great time at this party!'",
          options: [
            "Action (experiencing = action)",
            "State (possession)",
            "Both"
          ],
          a: "Action (experiencing = action)",
          successtip: "✓ IDIOM ALERT! 'Having a good time' = phrasal expression! 🎉 This is ACTION (experiencing), not possession! Idiom = special case!"
        }
      ]
    },

    // SET 6: Sense Verbs (Intermedio)
    {
      id: "m4-u5-set6",
      title: "Set 6: Five Senses",
      questions: [
        {
          type: "mc",
          q: "'This coffee ___ good.' (smell)",
          options: ["smells", "is smelling", "smell"],
          a: "smells",
          tip: "Sense verbs (see, hear, smell, taste, feel) = usually STATE when describing quality. 'It smells good' ✓"
        },
        {
          type: "input",
          q: "Complete: 'I ___ music from the next room.' (hear)",
          a: "hear"
        },
        {
          type: "order",
          q: "Ordena: 'Esta comida sabe deliciosa'",
          words: ["This", "food", "tastes", "delicious."],
          a: "This food tastes delicious."
        },
        {
          type: "mc",
          q: "ERROR: 'I am seeing the problem' (understand) - Corrección:",
          options: ["I see the problem", "I seeing the problem", "I sees the problem"],
          a: "I see the problem",
          tip: "'See' for UNDERSTANDING = state. 'I see' (entiendo) ✓. Physical seeing of object = also usually simple!"
        },
        {
          type: "match",
          q: "Match sense to state verb:",
          pairs: [
            { left: "Vista", right: "see" },
            { left: "Oído", right: "hear" },
            { left: "Olfato", right: "smell" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'She ___ the ocean from her window.' (see)",
          a: "sees"
        },
        {
          type: "mc",
          q: "'The soup ___ too salty.'",
          options: ["tastes", "is tasting", "taste"],
          a: "tastes",
          successtip: "✓ TASTE! Quality description! 👅 'It tastes good/bad' = state. BUT 'I'm tasting the soup' (action of trying) = continuous OK!"
        },
        {
          type: "order",
          q: "Ordena: 'Escucho ruido afuera'",
          words: ["I", "hear", "noise", "outside."],
          a: "I hear noise outside.",
          tip: "'Hear' = involuntary sense = state. 'Listen' (voluntary) can use continuous! 'I'm listening' ✓"
        },
        {
          type: "input",
          q: "Complete: 'This pillow ___ soft.' (feel)",
          a: "feels"
        },
        {
          type: "mc",
          q: "Exception: 'The chef ___ the sauce right now.' (testing it)",
          options: ["is tasting", "tastes", "taste"],
          a: "is tasting",
          successtip: "✓ TASTING! Action version! 👨‍🍳 When 'taste' = ACTION (physically trying food) = continuous OK! 'I'm tasting' = probando activamente!"
        }
      ]
    },

    // SET 7: Mental State Verbs (Intermedio)
    {
      id: "m4-u5-set7",
      title: "Set 7: Think, Know, Understand",
      questions: [
        {
          type: "mc",
          q: "'I ___ the instructions.' (understand - now)",
          options: ["understand", "am understanding", "understanding"],
          a: "understand",
          tip: "'Understand' = mental state = no continuous! Spanish 'Estoy entendiendo' ≠ English 'I understand' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She ___ three languages.' (know)",
          a: "knows"
        },
        {
          type: "order",
          q: "Ordena: 'No recuerdo su nombre'",
          words: ["I", "don't", "remember", "his", "name."],
          a: "I don't remember his name."
        },
        {
          type: "mc",
          q: "ERROR hispanohablante: 'I am needing more time' - Corrección:",
          options: ["I need more time", "I needing more time", "I needs more time"],
          a: "I need more time",
          tip: "Spanish 'Estoy necesitando' es común, but English 'I need' ✓! State verb = simple always!"
        },
        {
          type: "match",
          q: "Match mental state verb:",
          pairs: [
            { left: "know", right: "saber/conocer" },
            { left: "remember", right: "recordar" },
            { left: "forget", right: "olvidar" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'We ___ that you're right.' (realize)",
          a: "realize"
        },
        {
          type: "mc",
          q: "'I ___ about moving to Vancouver.' (consider)",
          options: ["am thinking", "think", "thinking"],
          a: "am thinking",
          successtip: "✓ THINKING! Special case! 🤔 'Think' for OPINION = simple ('I think so'). 'Think ABOUT' = considering = continuous OK! 'I'm thinking about it' ✓"
        },
        {
          type: "order",
          q: "Ordena: 'Reconozco esta canción'",
          words: ["I", "recognize", "this", "song."],
          a: "I recognize this song."
        },
        {
          type: "input",
          q: "Complete: 'He ___ where she lives.' (not know)",
          a: "doesn't know"
        },
        {
          type: "mc",
          q: "Which is CORRECT?",
          options: [
            "I'm thinking about my family back home",
            "I'm knowing the answer",
            "I'm understanding you"
          ],
          a: "I'm thinking about my family back home",
          tip: "'Think about' = considering (action) ✓. 'Know' and 'understand' = states (no -ING) ❌"
        }
      ]
    },

    // SET 8: Preference & Opinion (Intermedio)
    {
      id: "m4-u5-set8",
      title: "Set 8: Likes, Wants & Preferences",
      questions: [
        {
          type: "mc",
          q: "'I ___ working from home.' (prefer)",
          options: ["prefer", "am preferring", "preferring"],
          a: "prefer",
          tip: "'Prefer' = preference = state. 'I prefer' ✓, 'I am preferring' ❌"
        },
        {
          type: "input",
          q: "Complete: 'She ___ to become a nurse.' (want)",
          a: "wants"
        },
        {
          type: "order",
          q: "Ordena: 'Odio levantarme temprano'",
          words: ["I", "hate", "waking", "up", "early."],
          a: "I hate waking up early.",
          successtip: "✓ HATE + -ING! Verb pattern! 😫 Like/love/hate/prefer + verb-ING (gerund). 'I hate working nights' = common pattern!"
        },
        {
          type: "mc",
          q: "ERROR: 'She is wanting a better job' - Corrección:",
          options: ["She wants a better job", "She wanting a better job", "She want a better job"],
          a: "She wants a better job"
        },
        {
          type: "match",
          q: "Match preference verb to example:",
          pairs: [
            { left: "like", right: "I like coffee" },
            { left: "prefer", right: "I prefer tea" },
            { left: "hate", right: "I hate mornings" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'They ___ living in Toronto.' (love)",
          a: "love"
        },
        {
          type: "mc",
          q: "'I ___ pizza over burgers.'",
          options: ["prefer", "am preferring", "prefers"],
          a: "prefer"
        },
        {
          type: "order",
          q: "Ordena: 'Necesitamos más tiempo'",
          words: ["We", "need", "more", "time."],
          a: "We need more time."
        },
        {
          type: "input",
          q: "Complete: 'He ___ going to parties.' (not like)",
          a: "doesn't like"
        },
        {
          type: "mc",
          q: "Slang: 'I'm ___ it!' (McDonald's slogan - exception!)",
          options: ["loving", "love", "am love"],
          a: "loving",
          successtip: "✓ I'M LOVIN' IT! Famous exception! 🍔 McDonald's broke the rule for marketing! Normally 'I love it' ✓, but slogan = 'I'm loving it' for emphasis!"
        }
      ]
    },

    // SET 9: Exceptions & Advanced (Avanzado)
    {
      id: "m4-u5-set9",
      title: "Set 9: When State Verbs Act Like Actions",
      questions: [
        {
          type: "mc",
          q: "'I ___ about my future.' (think = considering)",
          options: ["am thinking", "think", "thinking"],
          a: "am thinking",
          tip: "EXCEPTION! 'Think ABOUT' = active mental process = CAN use continuous. 'I'm thinking about moving' ✓"
        },
        {
          type: "input",
          q: "Opinion: 'I ___ this is a good idea.' (think = opinion)",
          a: "think"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy viendo a mi doctor mañana' (appointment)",
          words: ["I'm", "seeing", "my", "doctor", "tomorrow."],
          a: "I'm seeing my doctor tomorrow.",
          successtip: "✓ SEEING! Exception! 👨‍⚕️ 'See' for APPOINTMENTS/DATING = continuous OK! 'I'm seeing someone' (dating) = common phrase!"
        },
        {
          type: "mc",
          q: "Dating context: 'I ___ a really nice person.' (romantic relationship)",
          options: ["am seeing", "see", "seeing"],
          a: "am seeing",
          tip: "'See' for DATING = continuous! 'I'm seeing someone' = estoy saliendo con alguien. Special meaning!"
        },
        {
          type: "match",
          q: "Match verb to when it CAN use continuous:",
          pairs: [
            { left: "think about (considering)", right: "I'm thinking about it" },
            { left: "have (experience)", right: "I'm having fun" },
            { left: "see (appointment)", right: "I'm seeing the dentist" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Experiencing: 'We ___ a great time!' (have)",
          a: "are having"
        },
        {
          type: "mc",
          q: "'She ___ very helpful today.' (behave - temporary)",
          options: ["is being", "is", "being"],
          a: "is being",
          successtip: "✓ BEING! Advanced exception! 😇 'Be' can use continuous for TEMPORARY behavior! 'You're being rude' = acting rude NOW (not always). High level!"
        },
        {
          type: "order",
          q: "Ordena: 'Estás siendo muy amable' (temporary behavior)",
          words: ["You're", "being", "very", "kind."],
          a: "You're being very kind."
        },
        {
          type: "input",
          q: "Tasting action: 'The chef ___ the soup.' (taste - action of trying)",
          a: "is tasting"
        },
        {
          type: "mc",
          q: "Which is CORRECT in context? 'I can't talk now, ___'",
          options: [
            "I'm having lunch",
            "I'm wanting lunch",
            "I'm needing lunch"
          ],
          a: "I'm having lunch",
          tip: "'Having lunch' = action (eating) ✓. 'Wanting' and 'needing' = states (never continuous) ❌"
        }
      ]
    },

    // SET 10: Real-Life Integration (Avanzado)
    {
      id: "m4-u5-set10",
      title: "Set 10: Mixing It All Together",
      questions: [
        {
          type: "mc",
          q: "'I ___ you ___ a mistake right now.' (think, make)",
          options: ["think, are making", "am thinking, make", "think, make"],
          a: "think, are making",
          tip: "'Think' = opinion (state) = simple. 'Making' = action happening now = continuous. Mix them!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ English but she ___ Chinese.' (know, not understand)",
          a: "knows, doesn't understand"
        },
        {
          type: "order",
          q: "Ordena: 'Sé que estás ocupado, pero necesito tu ayuda'",
          words: ["I", "know", "you're", "busy,", "but", "I", "need", "your", "help."],
          a: "I know you're busy, but I need your help.",
          successtip: "✓ MIXING TENSES! Real conversation! 💬 'Know' and 'need' = states (simple). 'You're busy' = temporary (continuous). Natural mix!"
        },
        {
          type: "mc",
          q: "'I ___ what you ___ but I ___ with you.' (understand, mean, not agree)",
          options: [
            "understand, mean, don't agree",
            "am understanding, are meaning, am not agreeing",
            "understand, are meaning, am not agreeing"
          ],
          a: "understand, mean, don't agree",
          tip: "All three = mental states = all simple! Understand, mean, agree = no continuous!"
        },
        {
          type: "match",
          q: "Match situation to correct form:",
          pairs: [
            { left: "I ___ a problem (have issue)", right: "have (simple)" },
            { left: "I ___ a party (experience)", right: "am having (continuous)" },
            { left: "I ___ you're right (believe)", right: "think (simple)" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Immigration context: 'I ___ for my work permit while I ___ for cash.' (wait, work)",
          a: "am waiting, am working"
        },
        {
          type: "mc",
          q: "Job interview: 'I ___ this position because I ___ working with people.'",
          options: ["want, love", "am wanting, am loving", "want, am loving"],
          a: "want, love",
          successtip: "✓ INTERVIEW ANSWER! Professional! 💼 Want + love = both states = simple! 'I want this job because I love teamwork' = perfect answer!"
        },
        {
          type: "order",
          q: "Ordena: 'Entiendo tu situación pero no puedo ayudarte ahora mismo porque estoy trabajando'",
          words: ["I", "understand", "your", "situation", "but", "I", "can't", "help", "you", "right", "now", "because", "I'm", "working."],
          a: "I understand your situation but I can't help you right now because I'm working."
        },
        {
          type: "input",
          q: "Complex: 'She ___ about buying a house, but she ___ if she ___ enough money.' (think, not know, have)",
          a: "is thinking, doesn't know, has"
        },
        {
          type: "mc",
          q: "Final integration: 'I ___ I ___ to move to Calgary, but I ___ what the weather ___ like.'",
          options: [
            "think, want, don't know, is",
            "am thinking, am wanting, am not knowing, is being",
            "think, am wanting, don't know, is"
          ],
          a: "think, want, don't know, is",
          successtip: "✓ UNIT 5 MASTERED! Verb types unlocked! 🎯 You now understand why 'I'm knowing' is wrong but 'I'm thinking about it' is right! State vs action = critical English skill! 🧠💪"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: Andrea's New Life (State vs. Action Verbs)
// ===================================================================
readingSet: {
  id: "m4-u5-reading",
  title: "Reading: Andrea's New Life",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Introduction & Emotions (preguntas 3 y 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Thinking about Life**

Hi! I'm Andrea and I'm an immigrant from Colombia living in Toronto. Right now, I'm sitting in a coffee shop and I'm thinking about my life here. 

I know it's difficult, but I believe things are getting better. I understand that the sponsorship process takes time, but I don't like waiting.`
    },
    {
      type: "mc",
      q: "Andrea dice 'I understand that the process takes time' - ¿Por qué no usa el -ing (continuous)?",
      options: [
        "'Understand' is a state verb (mental state)",
        "Debería ser 'I am understanding'",
        "Es un error del texto"
      ],
      a: "'Understand' is a state verb (mental state)",
      successtip: "✓ STATE VERB!\nVerbos mentales como *know, believe* y *understand* no suelen llevar -ing. 🧠"
    },
    {
      type: "mc",
      q: "¿Qué verbo usa Andrea para describir su emoción? 'I ___ her so much.'",
      options: ["miss", "am missing", "missing"],
      a: "miss",
      tip: "'Miss' = emotion = state verb! 'I miss you' ✓, no se dice 'I am missing you' ❌",
      successtip: "✓ CORRECT!\nLas emociones son estados permanentes en el presente simple. ❤️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Work & Family (preguntas 1, 2 y 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Working Hard**

I'm working two jobs at the moment. During the day, I work at a Tim Hortons, and at night, I'm doing cleaning shifts at an office building. 

I'm trying to save money because I want to bring my daughter here from Colombia. I miss her so much. I see her on video calls, but it's not the same.`
    },
    {
      type: "mc",
      q: "¿Cuántos trabajos tiene Andrea ahora?",
      options: ["Two jobs", "One job", "Three jobs"],
      a: "Two jobs",
      successtip: "✓ TWO JOBS!\nTim Hortons + Cleaning. El 'immigrant hustle' en acción. 💪"
    },
    {
      type: "mc",
      q: "¿Por qué está ahorrando dinero Andrea?",
      options: [
        "To bring her daughter from Colombia",
        "To buy a house",
        "To buy a car"
      ],
      a: "To bring her daughter from Colombia",
      successtip: "✓ FAMILY REUNION!\nElla está ahorrando para el proceso de patrocinio (sponsorship). 🇨🇴✈️🇨🇦"
    },
    {
      type: "mc",
      q: "En la frase 'I see her on video calls' - ¿Por qué usa 'see' en simple y no 'seeing'?",
      options: [
        "'See' aquí es un state verb (un hecho general)",
        "Es un error de dedo",
        "Debería ser 'I'm seeing'"
      ],
      a: "'See' aquí es un state verb (un hecho general)",
      successtip: "✓ SEE!\n'I see' (veo/entiendo) es un estado. 'I am seeing' se usa más para citas o compromisos. 👀"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Education & Career (pregunta 4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Future Professional**

I'm learning English, and I'm getting better every day. I prefer speaking English now because I want to improve. 

I'm also taking accounting classes at night school because I was an accountant in Colombia. I don't have my Canadian certification yet, but I'm working on it.`
    },
    {
      type: "mc",
      q: "¿Cuál es la profesión original de Andrea en Colombia?",
      options: ["Accountant", "Teacher", "Nurse"],
      a: "Accountant",
      successtip: "✓ ACCOUNTANT!\nContadora. Muchos profesionales deben recertificarse al llegar a Canadá. 📊"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Reflection & Grammar (preguntas 9, 7, 8 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Staying Positive**

I know I'm working hard, and I realize I'm tired all the time. But I'm having a better life than before, and I believe my daughter will be here soon. I'm staying positive!`
    },

    

    {
      type: "mc",
      q: "Andrea dice 'I'm having a better life' - ¿Por qué aquí 'having' sí puede llevar -ing?",
      options: [
        "'Having' aquí significa 'experimentando' (acción), no 'poseyendo'",
        "Es un error gramatical",
        "El verbo 'Have' siempre puede llevar -ing"
      ],
      a: "'Having' aquí significa 'experimentando' (acción), no 'poseyendo'",
      tip: "Have (posesión) = Simple. Have (experiencia/acción) = Continuous OK! 💡",
      successtip: "✓ DYNAMIC HAVE!\n'I have a car' (posesión) vs 'I am having a good time' (experiencia). 🌟"
    },
    {
      type: "mc",
      q: "¿Cuántos 'state verbs' (verbos que describen estados/sentidos) identificas en el texto total?",
      options: [
        "Más de 10 (know, believe, want, miss, understand, like, prefer, etc.)",
        "Solo 2 o 3",
        "Ninguno"
      ],
      a: "Más de 10 (know, believe, want, miss, understand, like, prefer, etc.)",
      successtip: "✓ SHARP EYE!\nEl texto está lleno de verbos que describen lo que ella piensa o siente. 🧠"
    },
    {
      type: "mc",
      q: "¿Cuántos 'action verbs' en presente continuo (-ing) identificas?",
      options: [
        "7-9 (sitting, thinking, working, doing, trying, learning, getting, taking, staying)",
        "Solo 1 o 2",
        "Más de 20"
      ],
      a: "7-9 (sitting, thinking, working, doing, trying, learning, getting, taking, staying)",
      successtip: "✓ ACTION!\nEstos describen las actividades que ella está realizando activamente para progresar. 🏃‍♀️"
    },
    {
      type: "mc",
      q: "En conclusión, ¿qué demuestra este tipo de texto sobre el inglés real?",
      options: [
        "Que mezclamos estados (simple) y acciones (continuous) naturalmente",
        "Que solo debemos usar el presente simple",
        "Que el inglés de los inmigrantes tiene muchos errores"
      ],
      a: "Que mezclamos estados (simple) y acciones (continuous) naturalmente",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Entendiste cómo usar el Simple para hechos/emociones y el Continuous para acciones temporales. 🏆"
    }
  ]
},

  // REVIEW SET
  reviewSet: {
    id: "m4-u5-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Action verbs son:",
        options: [
          "Acciones físicas que puedes ver (eat, run, work)",
          "Estados mentales y emociones",
          "Solo verbos con -ING"
        ],
        a: "Acciones físicas que puedes ver (eat, run, work)"
      },
      {
        type: "match",
        q: "Match verb type:",
        pairs: [
          { left: "Action verb", right: "Can use -ING" },
          { left: "State verb", right: "Usually NO -ING" },
          { left: "Exception", right: "Some state verbs have action meanings" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Which is INCORRECT?",
        options: [
          "I am knowing the answer",
          "I am eating lunch",
          "I am thinking about it"
        ],
        a: "I am knowing the answer",
        tip: "'Know' = state (never continuous). 'Eating' = action ✓. 'Thinking about' = exception ✓"
      },
      {
        type: "input",
        q: "State verb: 'I ___ coffee.' (like)",
        a: "like"
      },
      {
        type: "mc",
        q: "'Have' for possession:",
        options: ["Simple (I have a car)", "Continuous (I'm having a car)", "Both are correct"],
        a: "Simple (I have a car)"
      },
      {
        type: "order",
        q: "Ordena: 'Entiendo lo que estás diciendo'",
        words: ["I", "understand", "what", "you're", "saying."],
        a: "I understand what you're saying."
      },
      {
        type: "mc",
        q: "Exception: 'I'm thinking ___' can use continuous when:",
        options: [
          "Followed by 'about' (considering)",
          "Never",
          "Always"
        ],
        a: "Followed by 'about' (considering)"
      },
      {
        type: "input",
        q: "Action verb: 'She ___ dinner right now.' (cook)",
        a: "is cooking"
      },
      {
        type: "mc",
        q: "Common error: 'I am needing help' should be:",
        options: ["I need help", "I needing help", "I needs help"],
        a: "I need help",
        tip: "Spanish 'Estoy necesitando' ≠ English! 'Need' = state = simple always!"
      },
      {
        type: "mc",
        q: "Final test: 'I ___ you ___ but I ___ what you ___.' (know, work, not understand, mean)",
        options: [
          "know, are working, don't understand, mean",
          "am knowing, work, am not understanding, are meaning",
          "know, work, don't understand, are meaning"
        ],
        a: "know, are working, don't understand, mean",
        successtip: "✓ UNIT 5 COMPLETE! State vs Action mastered! 🎉 You now understand why some verbs use -ING and others don't! No more 'I am knowing' or 'I am needing'! This is high-level English grammar! 🧠🔥🇨🇦"
      }
    ]
  }
};
